/**
 * A reading for every screen that has numbers on it.
 *
 * The friction and rubber analysers proved the shape: compute from the data the
 * page itself draws, say what it implies, offer what could be done with what
 * each costs. This extends that to the rest of the platform, and the same three
 * rules hold throughout.
 *
 * **Arithmetic, not adjectives.** Every figure below is counted from the
 * records. Where the platform says it could not work something out, that is
 * reported as its own fact rather than folded into a total — an obligation with
 * no evidence and an obligation that failed are different, and a screen that
 * adds them has destroyed the distinction its own designers preserved.
 *
 * **The bottleneck, not the list.** A count of open work is not a reading; the
 * reading is which state the work is piling up in. Twelve open with eight
 * awaiting verification is a verification problem, and saying "twelve open"
 * hides that. Each analyser below tries to name the constraint.
 *
 * **Options with costs, never a decision.** Same rule as everywhere else in the
 * module. She lays out what could be done and what it would cost; the pressing
 * is the operator's.
 *
 * Where a screen has no numbers of its own — the aerodrome drawing, the 3-D
 * section, the engineering checklist — she says so and names what she *can*
 * read, rather than dressing a description up as an analysis.
 */
import type { DataSource, Principal } from "../api/dataSource";
import { PERMISSION, hasPermission } from "../api/dataSource";
import { isOpen, priorityLabel } from "../domain/workOrders";
import type { PageAnswer } from "./pages";
import type { Suggestion } from "./types";
import { citationLines, headlineStandard } from "./standards";

export interface SectionContext {
  source: DataSource;
  siteId: string;
  siteCode: string;
  principal: Principal;
  now: number;
}

interface Section {
  id: string;
  routes: readonly string[];
  /** The permission the platform requires to read this at all. */
  needs?: string;
  analyse: (ctx: SectionContext) => Promise<PageAnswer | null>;
}

const days = (iso: string | undefined, now: number): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.round((now - t) / 86_400_000) : null;
};
const pct = (n: number, of: number) => (of === 0 ? 0 : Math.round((n / of) * 100));

/* ── work orders ──────────────────────────────────────────────────────── */

const workOrders: Section = {
  id: "work-orders",
  routes: ["/work-orders"],
  needs: PERMISSION.workOrdersRead,
  async analyse({ source, siteId, siteCode, now }) {
    const page = await source.listWorkOrders({ siteId });
    const all = page.items;
    if (all.length === 0) return null;
    const open = all.filter((w) => isOpen(w.status));
    const byStatus = new Map<string, number>();
    for (const w of open) byStatus.set(w.status, (byStatus.get(w.status) ?? 0) + 1);
    const p1 = open.filter((w) => w.priority === 1);
    const overdue = open.filter((w) => {
      const d = days(w.dueAt, now);
      return d !== null && d > 0;
    });
    const awaitingVerify = all.filter((w) => w.status === "completed").length;
    const ages = open.map((w) => days(w.openedAt, now) ?? 0);
    const oldest = ages.length ? Math.max(...ages) : 0;
    const median = ages.length ? [...ages].sort((a, b) => a - b)[Math.floor(ages.length / 2)] : 0;

    /* The constraint, not the count. Whichever state holds the most open work
       is where the queue is actually stuck. */
    const [stuckState, stuckCount] = [...byStatus.entries()].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];

    const reasoning: string[] = [];
    if (stuckCount > 0) {
      reasoning.push(
        `Most of the open work sits at “${stuckState}” (${stuckCount} of ${open.length}) — that is where the queue is held, not at the total.`,
      );
    }
    if (awaitingVerify > 0) {
      reasoning.push(
        `${awaitingVerify} ${awaitingVerify === 1 ? "order is" : "orders are"} completed and waiting on verification. The work is done; the record is not closed, and the KPI will not move until it is.`,
      );
    }
    if (overdue.length > 0) {
      reasoning.push(
        `${overdue.length} past ${overdue.length === 1 ? "its" : "their"} due date, ${overdue.filter((w) => w.priority === 1).length} of them P1. A due date that passes without a note is the one that stops being believed.`,
      );
    }
    if (oldest > 30) {
      reasoning.push(
        `Oldest open is ${oldest} days; the median is ${median}. The tail is the problem, not the middle.`,
      );
    }

    const suggestions: Suggestion[] = [];
    if (awaitingVerify > 0) {
      suggestions.push({
        id: "wo-verify",
        label: `Clear the ${awaitingVerify} awaiting verification`,
        because: "Finished work that is not verified reads as open everywhere else.",
        tradeOff:
          "Needs work_order:verify and somebody who did not do the work. That separation is the point of the step.",
      });
    }
    if (overdue.length > 0) {
      suggestions.push({
        id: "wo-overdue",
        label: "Re-date or re-prioritise what has run past its due date",
        because: `${overdue.length} overdue.`,
        tradeOff:
          "An honest later date is worth more than an optimistic one that already passed. Moving it also loses the evidence of how long it really took.",
      });
    }
    if (byStatus.get("on_hold")) {
      suggestions.push({
        id: "wo-holds",
        label: `Review the ${byStatus.get("on_hold")} on hold`,
        because: "A hold with no reason left on it is indistinguishable from work that was forgotten.",
        tradeOff: "Costs a read of each. Some will still be legitimately held.",
      });
    }

    return {
      brief: [
        `${siteCode}: ${open.length} open of ${all.length}, ${p1.length} at P1, ${overdue.length} overdue.`,
        ...reasoning.slice(0, 2),
      ],
      full: [
        `${siteCode} work orders: ${all.length} on file, ${open.length} open.`,
        `By state: ${[...byStatus.entries()].map(([k, v]) => `${k} ${v}`).join(", ")}.`,
        `Priorities open: ${[1, 2, 3, 4, 5].map((p) => `${priorityLabel(p as 1)} ${open.filter((w) => w.priority === p).length}`).join(", ")}.`,
        `Ages: oldest ${oldest} days, median ${median}.`,
        "",
        "What that says:",
        ...reasoning,
      ],
      suggestions,
      sourceLabel: `Work orders · ${siteCode}`,
      sourceDetail: `${all.length} records`,
    };
  },
};

/* ── alerts ───────────────────────────────────────────────────────────── */

const alerts: Section = {
  id: "alerts",
  routes: ["/alerts"],
  needs: PERMISSION.insightRead,
  async analyse({ source, siteId, siteCode, now }) {
    const [list, wos] = await Promise.all([
      source.listAlerts(siteId),
      source.listWorkOrders({ siteId }).catch(() => ({ items: [], truncated: false })),
    ]);
    const open = list.filter((a) => a.status === "open");
    if (list.length === 0) return null;
    const raisedFrom = new Set(wos.items.map((w) => w.alertId).filter(Boolean));
    const unraised = open.filter((a) => !raisedFrom.has(a.id));
    const severe = open.filter((a) => a.severity >= 4);
    const predictive = open.filter((a) => a.kind === "predictive");
    const oldest = Math.max(0, ...open.map((a) => days(a.raisedAt, now) ?? 0));

    const reasoning: string[] = [];
    if (unraised.length > 0) {
      reasoning.push(
        `${unraised.length} of the ${open.length} open alerts ${unraised.length === 1 ? "has" : "have"} no work order raised from ${unraised.length === 1 ? "it" : "them"} — ${unraised.filter((a) => a.severity >= 4).length} at severity 4 or 5. An alert nobody acted on is the rule working and the response not.`,
      );
    } else {
      reasoning.push("Every open alert has work raised from it. The rules and the response are in step.");
    }
    if (predictive.length > 0) {
      reasoning.push(
        `${predictive.length} ${predictive.length === 1 ? "is" : "are"} predictive — the fault is expected, not observed. Those are confirmed by the work order completing and refuted by it being cancelled, which is what makes the precision figure mean anything.`,
      );
    }
    if (oldest > 14) reasoning.push(`The oldest open alert is ${oldest} days old.`);

    return {
      brief: [
        `${siteCode}: ${open.length} open alerts, ${severe.length} at severity 4–5, ${unraised.length} with nothing raised from them.`,
        ...reasoning.slice(0, 2),
      ],
      full: [
        `${siteCode}: ${open.length} open of ${list.length} alerts on file.`,
        `By severity: ${[5, 4, 3, 2, 1].map((s) => `S${s} ${open.filter((a) => a.severity === s).length}`).join(", ")}.`,
        `${predictive.length} predictive, ${open.length - predictive.length} reactive. Oldest open ${oldest} days.`,
        "",
        "What that says:",
        ...reasoning,
        ...(unraised.length
          ? ["", "Nothing raised from:", ...unraised.slice(0, 5).map((a) => `• S${a.severity} — ${a.title}`)]
          : []),
      ],
      suggestions: unraised.length
        ? [
            {
              id: "al-raise",
              label: "Raise work against the severe alerts with nothing on them",
              because: `${unraised.filter((a) => a.severity >= 4).length} at severity 4–5 with no work order.`,
              tradeOff:
                "Puts them on the record and in the queue. It also commits crews, and a noisy rule will keep producing these until the rule is looked at.",
            },
            {
              id: "al-rule",
              label: "Look at the rule instead, if these keep coming back",
              because:
                "A rule that fires repeatedly and is repeatedly closed is a threshold problem, not a maintenance problem.",
              tradeOff: "Changing a threshold needs the certification basis behind it; it is not a screen setting.",
            },
          ]
        : undefined,
      sourceLabel: `Alerts · ${siteCode}`,
      sourceDetail: `${list.length} alerts`,
    };
  },
};

/* ── inspections ──────────────────────────────────────────────────────── */

const inspections: Section = {
  id: "inspections",
  routes: ["/inspections", "/inspection-platform"],
  needs: PERMISSION.inspectionsRead,
  async analyse({ source, siteId, siteCode, now }) {
    const list = await source.listInspections({ siteId });
    if (list.length === 0) return null;
    const byStatus = new Map<string, number>();
    for (const i of list) byStatus.set(i.status, (byStatus.get(i.status) ?? 0) + 1);
    /* The platform's own inspection states: scheduled, in_progress, completed,
       cancelled. "Closed" is the library's word, not this one's. */
    const open = list.filter((i) => i.status === "scheduled" || i.status === "in_progress");
    const overdue = open.filter((i) => {
      const d = days(i.scheduledFor, now);
      return d !== null && d > 0;
    });

    const reasoning = [
      overdue.length > 0
        ? `${overdue.length} scheduled ${overdue.length === 1 ? "sheet is" : "sheets are"} past ${overdue.length === 1 ? "its" : "their"} date. A missed inspection is not a finding of nothing wrong — it is an absence of evidence, and the compliance screen counts it as one.`
        : "Nothing scheduled has been missed.",
      "Items that fail carry forward as defects; closing a sheet does not close them, so the count of closed sheets is not a count of resolved findings.",
    ];

    return {
      brief: [
        `${siteCode}: ${list.length} inspections, ${open.length} open, ${overdue.length} past their date.`,
        reasoning[0],
      ],
      full: [
        `${siteCode}: ${list.length} inspections on file.`,
        `By state: ${[...byStatus.entries()].map(([k, v]) => `${k} ${v}`).join(", ")}.`,
        "",
        "What that says:",
        ...reasoning,
      ],
      suggestions: overdue.length
        ? [
            {
              id: "insp-catch",
              label: "Catch up the missed sheets",
              because: `${overdue.length} past their scheduled date.`,
              tradeOff:
                "Restores the evidence. Doing them late is still better than a gap, but the gap stays on the record.",
            },
            {
              id: "insp-interval",
              label: "Question the interval instead",
              because: "A schedule that is routinely missed is a schedule nobody agreed to.",
              tradeOff:
                "Lengthening an interval is a safety argument, not a convenience one, and it needs the pack changed.",
            },
          ]
        : undefined,
      sourceLabel: `Inspections · ${siteCode}`,
      sourceDetail: `${list.length} sheets`,
    };
  },
};

/* ── compliance ───────────────────────────────────────────────────────── */

const compliance: Section = {
  id: "compliance",
  routes: ["/compliance"],
  needs: PERMISSION.insightRead,
  async analyse({ source, siteId, siteCode }) {
    const report = await source.getCompliance(siteId);
    const obs = report.obligations ?? [];
    if (obs.length === 0) return null;
    const met = obs.filter((o) => o.status === "met").length;
    const open = obs.filter((o) => o.status !== "met");
    const noEvidence = obs.filter((o) => o.evidenceCount === 0).length;

    const reasoning = [
      `${met} of ${obs.length} periods met (${pct(met, obs.length)}%). Every period is listed whether or not it was met, so what is missing is visible rather than absent — a screen that only listed successes would show 100%.`,
      noEvidence > 0
        ? `${noEvidence} ${noEvidence === 1 ? "period has" : "periods have"} no evidence attached at all. That is not a failure; it is an unanswerable question, and it is the one to close first because it cannot be argued either way.`
        : "Every period carries evidence.",
    ];

    return {
      brief: [
        `${siteCode}: ${met}/${obs.length} obligation periods met, ${open.length} open, ${noEvidence} with no evidence.`,
        reasoning[1],
      ],
      full: [
        `${siteCode} compliance: ${obs.length} obligation periods across ${new Set(obs.map((o) => o.framework)).size} frameworks.`,
        `Met ${met}. Open ${open.length}. No evidence ${noEvidence}.`,
        "",
        "What that says:",
        ...reasoning,
        ...(open.length
          ? [
              "",
              "Open periods:",
              ...open.slice(0, 5).map((o) => `• ${o.framework} — ${o.title}${o.periodKey ? ` (${o.periodKey})` : ""}`),
            ]
          : []),
      ],
      suggestions: noEvidence
        ? [
            {
              id: "comp-evidence",
              label: "Attach evidence to the periods that have none",
              because: "An unanswerable period is worse than a failed one — nobody can tell which it was.",
              tradeOff: "Costs the time to find and attach it. It does not make a missed obligation met.",
            },
          ]
        : undefined,
      sourceLabel: `Compliance · ${siteCode}`,
      sourceDetail: `${obs.length} periods`,
    };
  },
};

/* ── the dashboard ────────────────────────────────────────────────────── */

const dashboard: Section = {
  id: "dashboard",
  routes: ["/", "/overview"],
  needs: PERMISSION.insightRead,
  async analyse({ source, siteId, siteCode }) {
    const kpis = await source.listKpis(siteId);
    if (kpis.length === 0) return null;
    const computable = kpis.filter((k) => k.value !== null);
    const uncomputable = kpis.filter((k) => k.value === null);
    const noTarget = computable.filter((k) => k.target === null);
    const offTarget = computable.filter(
      (k) => k.target !== null && (k.direction === "up" ? k.value! < k.target : k.value! > k.target),
    );

    const graded = computable.length - noTarget.length;
    const uncomputableLine =
      uncomputable.length > 0
        ? `${uncomputable.length} cannot be computed at all and ${uncomputable.length === 1 ? "is" : "are"} excluded from the grade rather than counted as zero. A zero there would read as good news and be a lie.`
        : "Every measure could be computed.";
    const reasoning = [
      `${graded - offTarget.length} of ${graded} measures with a target are on target. ${offTarget.length} ${offTarget.length === 1 ? "is" : "are"} not.`,
      ...(noTarget.length > 0
        ? [
            `${noTarget.length} ${noTarget.length === 1 ? "has" : "have"} no declared target, so ${noTarget.length === 1 ? "it is" : "they are"} shown but not graded — on or off target cannot be said of a measure nobody set a target for.`,
          ]
        : []),
      ...(() => {
        const proposed = computable.filter((k) => k.targetStatus === "proposed");
        return proposed.length > 0
          ? [
              `${proposed.length} of those targets ${proposed.length === 1 ? "is" : "are"} proposed, not agreed — a grade against ${proposed.length === 1 ? "it" : "them"} is a starting point, not a verdict: ${proposed.map((k) => k.label).join(", ")}.`,
            ]
          : [];
      })(),
      uncomputableLine,
    ];
    if (offTarget.length) {
      reasoning.push(
        `Furthest from target: ${offTarget
          .map((k) => k.label)
          .slice(0, 3)
          .join(", ")}.`,
      );
    }

    return {
      brief: [
        `${siteCode}: ${offTarget.length} of ${graded} graded measures off target, ${noTarget.length} with no target, ${uncomputable.length} not computable.`,
        uncomputableLine,
      ],
      full: [
        `${siteCode} executive KPIs: ${kpis.length} measures.`,
        "",
        "What that says:",
        ...reasoning,
        ...(uncomputable.length
          ? [
              "",
              "Not computable, and why:",
              ...uncomputable.map((k) => `• ${k.label} — ${k.reason ?? "no reason given"}`),
            ]
          : []),
      ],
      suggestions: uncomputable.length
        ? [
            {
              id: "kpi-uncomputable",
              label: "Chase what the uncomputable measures are missing",
              because: "A measure nobody can compute is a question nobody is answering.",
              tradeOff: "Usually a data gap rather than a maintenance one, so the fix is upstream and slower.",
            },
          ]
        : undefined,
      sourceLabel: `KPIs · ${siteCode}`,
      sourceDetail: `${kpis.length} measures`,
    };
  },
};

/* ── programme ────────────────────────────────────────────────────────── */

const programme: Section = {
  id: "programme",
  routes: ["/programme"],
  needs: PERMISSION.insightRead,
  async analyse({ source, siteId, siteCode, now }) {
    const p = await source.programme(siteId, new Date(now).getFullYear());
    if (!p.available) return null;
    const undecidable = p.undecidable ?? [];
    const deferred = p.deferred ?? [];
    const scheduled = p.scheduled ?? [];

    const reasoning = [
      `${scheduled.length} scheduled, ${deferred.length} deferred, ${undecidable.length} the platform declined to decide.`,
      undecidable.length > 0
        ? `The ${undecidable.length} undecidable ${undecidable.length === 1 ? "job is" : "jobs are"} the important number: they are held up by a missing fact, not by money, so a bigger envelope does not move them. Somebody has to answer a question.`
        : "Nothing is blocked on a missing fact — what is deferred is deferred on money.",
    ];

    return {
      brief: [
        `${siteCode} programme: ${scheduled.length} scheduled, ${deferred.length} deferred, ${undecidable.length} undecidable.`,
        reasoning[1],
      ],
      full: [
        `${siteCode} programme for FY${p.fiscalYear}${p.envelope ? `, envelope ${p.envelope.toLocaleString()}` : ""}.`,
        `Scheduled ${scheduled.length}. Deferred ${deferred.length}. Undecidable ${undecidable.length}.`,
        "",
        "What that says:",
        ...reasoning,
      ],
      suggestions: undecidable.length
        ? [
            {
              id: "prog-facts",
              label: "Work the undecidable list before arguing the envelope",
              because: "More money does not move a job that is waiting on a fact.",
              tradeOff:
                "Each needs a person at a desk to answer something. It is slower than funding and it is the only thing that works.",
            },
          ]
        : undefined,
      sourceLabel: `Programme · ${siteCode}`,
      sourceDetail: `FY${p.fiscalYear}`,
    };
  },
};

/* ── capital projects ─────────────────────────────────────────────────── */

const projects: Section = {
  id: "projects",
  routes: ["/capital-projects", "/projects"],
  needs: PERMISSION.projectsRead,
  async analyse({ source, siteId, siteCode }) {
    const [rows, holds] = await Promise.all([
      source.listProjects({ siteId }),
      source.listHoldPoints(siteId).catch(() => null),
    ]);
    if (rows.length === 0) return null;

    const reasoning: string[] = [];
    if (holds) {
      reasoning.push(
        holds.state === "work_stopped"
          ? `${holds.count} hold ${holds.count === 1 ? "point is" : "points are"} placed and work is stopped. A hold point is a signature somebody required before the next thing happens; releasing one is a separate, separately audited act from recording what an inspection found.`
          : `No hold point is holding. That is a statement about hold points only — it is not a clearance to proceed.`,
      );
    }
    reasoning.push(
      "Money on a project is as declared. The platform computes nothing from it and neither does the screen, so a total here is a sum of what people typed.",
    );

    return {
      brief: [
        `${siteCode}: ${rows.length} projects${holds ? `, ${holds.count} hold point${holds.count === 1 ? "" : "s"} placed` : ""}.`,
        ...reasoning.slice(0, 1),
      ],
      full: [
        `${siteCode}: ${rows.length} capital projects.`,
        `By status: ${[...new Set(rows.map((r) => r.status))].map((st) => `${st} ${rows.filter((r) => r.status === st).length}`).join(", ")}.`,
        ...(holds ? [`Hold points: ${holds.count} placed, ${holds.holdPointsEver} ever. State: ${holds.state}.`] : []),
        "",
        "What that says:",
        ...reasoning,
      ],
      suggestions:
        holds && holds.state === "work_stopped"
          ? [
              {
                id: "proj-holds",
                label: "Work the hold points that are stopping work",
                because: `${holds.count} placed and work stopped.`,
                tradeOff:
                  "Each needs the inspection recorded and then, separately, the release signed. The separation is what the hold point was placed to require — collapsing the two is the failure it exists to prevent.",
              },
            ]
          : undefined,
      sourceLabel: `Projects · ${siteCode}`,
      sourceDetail: `${rows.length} projects`,
    };
  },
};

/* ── drift ────────────────────────────────────────────────────────────── */

const drift: Section = {
  id: "drift",
  routes: ["/drift"],
  needs: PERMISSION.driftRead,
  async analyse({ source, siteId, siteCode }) {
    const checks = await source.driftChecks(siteId);
    if (checks.length === 0) return null;
    const undecided = checks.reduce((n, c) => n + (c.undecided ?? 0), 0);
    const pending = checks.reduce((n, c) => n + (c.pending ?? 0), 0);
    const predictions = checks.reduce((n, c) => n + (c.predictions ?? 0), 0);
    const decided = checks.reduce((n, c) => n + (c.decided ?? 0), 0);

    const reasoning = [
      `${checks.length} checks over ${predictions} predictions: ${decided} judged, ${pending} not yet knowable, ${undecided} undecided.`,
      pending > 0
        ? `The ${pending} pending are not wrong — their horizon has not elapsed. Counting them as failures is how a model gets retired for being recent.`
        : "Every prediction in the window could be judged.",
      "A model that was right last year and is wrong now fails quietly. That is what these checks exist to make loud.",
    ];

    return {
      brief: [
        `${siteCode}: ${checks.length} drift checks, ${decided} of ${predictions} predictions judged.`,
        reasoning[1],
      ],
      full: [`${siteCode} model drift.`, "", "What that says:", ...reasoning],
      sourceLabel: `Drift · ${siteCode}`,
      sourceDetail: `${checks.length} checks`,
    };
  },
};

/* ── reliability ──────────────────────────────────────────────────────── */

const reliability: Section = {
  id: "reliability",
  routes: ["/reliability"],
  needs: PERMISSION.reliabilityRead,
  async analyse({ source, siteId, siteCode }) {
    const pops = await source.populations(siteId);
    if (pops.length === 0) return null;
    const fittable = pops.filter((p) => p.fittable);
    const units = pops.reduce((n, p) => n + (p.units ?? 0), 0);
    const failures = pops.reduce((n, p) => n + (p.failures ?? 0), 0);

    const reasoning = [
      `${pops.length} populations, ${units} units, ${failures} failures. ${fittable.length} can be fitted; ${pops.length - fittable.length} cannot — too little to fit is reported as that, not as a fit of zero.`,
      "Both units and failures are carried. The gap between them is what a failures-only fit throws away: fit only the things that broke and you learn how broken things break, not how the fleet does.",
    ];

    return {
      brief: [
        `${siteCode}: ${pops.length} populations, ${fittable.length} fittable, ${failures} failures across ${units} units.`,
        reasoning[1],
      ],
      full: [
        `${siteCode} reliability.`,
        "",
        "What that says:",
        ...reasoning,
        ...(pops.length - fittable.length > 0
          ? [
              "",
              "Not fittable, and why:",
              ...pops
                .filter((p) => !p.fittable)
                .slice(0, 5)
                .map((p) => `• ${p.population} — ${p.whyNot ?? "not stated"}`),
            ]
          : []),
      ],
      sourceLabel: `Reliability · ${siteCode}`,
      sourceDetail: `${pops.length} populations`,
    };
  },
};

/* ── the ledger ───────────────────────────────────────────────────────── */

const ledger: Section = {
  id: "ledger",
  routes: ["/ledger"],
  needs: PERMISSION.ledgerRead,
  async analyse({ source, siteId, siteCode }) {
    const facts = await source.ledgerFacts(siteId);
    if (facts.length === 0) return null;
    const comparable = facts.reduce((n, f) => n + (f.comparableSubjects ?? 0), 0);
    const subjects = facts.reduce((n, f) => n + (f.subjects ?? 0), 0);
    const withAuthority = facts.filter((f) => f.authority).length;

    const reasoning = [
      `${facts.length} fact keys over ${subjects} subjects, of which ${comparable} were reported by more than one party.`,
      `Only those ${comparable} can diverge at all. The other ${subjects - comparable} are one party talking — which is not agreement, and is not a dispute either.`,
      `${withAuthority} of the fact keys have an agreed authority; the rest do not, so a disagreement in them has no tie-break.`,
    ];

    return {
      brief: [`${siteCode}: ${comparable} of ${subjects} subjects are comparable across parties.`, reasoning[1]],
      full: [`${siteCode} ledger.`, "", "What that says:", ...reasoning],
      sourceLabel: `Ledger · ${siteCode}`,
      sourceDetail: `${facts.length} fact keys`,
    };
  },
};

/* ── liability ────────────────────────────────────────────────────────── */

const liability: Section = {
  id: "liability",
  routes: ["/liability"],
  needs: PERMISSION.insightRead,
  async analyse({ source, siteId, siteCode }) {
    const reg = await source.liabilityRegister(siteId);
    if (!reg || reg.candidates === 0) return null;
    const cover = pct(reg.priced, reg.candidates);

    const reasoning = [
      `${reg.priced} of ${reg.candidates} candidates are priced — ${cover}% of the register.`,
      cover < 100
        ? `The curve therefore covers ${cover}% of what is deferred. It is that fraction of an answer, and it is not rounded up: the ${reg.notPriced} unpriced are not zero-cost, they are unknown-cost.`
        : "Every candidate is priced, so the curve covers the whole register.",
    ];

    return {
      brief: [`${siteCode}: ${reg.candidates} deferral candidates, ${reg.priced} priced (${cover}%).`, reasoning[1]],
      full: [
        `${siteCode} liability register.`,
        "",
        "What that says:",
        ...reasoning,
        ...(reg.note ? ["", reg.note] : []),
      ],
      suggestions:
        reg.notPriced > 0
          ? [
              {
                id: "liab-price",
                label: `Price the ${reg.notPriced} unpriced candidates`,
                because: "An unpriced candidate silently reads as free in any total built from the priced ones.",
                tradeOff:
                  "Estimating costs time and the estimate carries its own error — but a stated estimate can be argued with, and a gap cannot.",
              },
            ]
          : undefined,
      sourceLabel: `Liability · ${siteCode}`,
      sourceDetail: `${reg.candidates} candidates`,
    };
  },
};

/* ── the registry ─────────────────────────────────────────────────────── */

const SECTIONS: readonly Section[] = [
  workOrders,
  alerts,
  inspections,
  compliance,
  dashboard,
  programme,
  projects,
  drift,
  reliability,
  ledger,
  liability,
];

export function sectionFor(route: string): { id: string; needs?: string } | null {
  const s = SECTIONS.find((x) => x.routes.includes(route));
  return s ? { id: s.id, needs: s.needs } : null;
}

/**
 * The reading for a screen, or null when there is none to give.
 *
 * Null is a real answer here and is treated as one by the caller. Three screens
 * out of seventeen are drawings rather than datasets — the aerodrome plan, the
 * 3-D section, the engineering checklist — and inventing a statistic for them
 * would be worse than saying there is nothing to count.
 */
export async function analyseSection(route: string, ctx: SectionContext): Promise<PageAnswer | null> {
  const section = SECTIONS.find((s) => s.routes.includes(route));
  if (!section) return null;
  if (section.needs && !hasPermission(ctx.principal, section.needs)) {
    return {
      brief: [`This screen needs ${section.needs}, which your sign-in does not carry — so I have not read it.`],
      full: [
        `This screen needs ${section.needs}.`,
        "",
        "Your sign-in does not carry it, so I have not read the data behind it. The platform would refuse me the same way it refuses the screen; telling you what is on it anyway would be handing you a record you were not given.",
      ],
      sourceLabel: "Permission",
      sourceDetail: section.needs,
    };
  }
  try {
    const answer = await section.analyse(ctx);
    if (!answer) return null;
    /*
     * The regulatory frame, added once here rather than in eleven analysers.
     *
     * Reading the data back is half an answer — the other half is which level
     * it has crossed and whose table that level is from. The brief gets one
     * line of it; the report gets the citations, because a reference an
     * operator cannot look up is decoration.
     */
    const headline = headlineStandard(section.id);
    return {
      ...answer,
      /* Two lines of reading and one of rule. Appending rather than replacing
         grew the brief past the point where it could be read at a glance. */
      brief: headline ? [...answer.brief.slice(0, 2), headline] : answer.brief,
      full: [...answer.full, ...citationLines(section.id)],
    };
  } catch {
    return null;
  }
}
